import React, { useState, ReactNode, useRef, useEffect, useCallback } from "react";
import "./Tooltip.css";

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  delay?: number;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  delay = 300,
  position = "top",
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      setIsVisible(true);
      calculatePosition();
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
  };

  const calculatePosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - 10;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + 10;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - 10;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + 10;
        break;
    }

    // Adjust if tooltip would go off screen
    const rightEdge = left + tooltipRect.width;
    const bottomEdge = top + tooltipRect.height;

    if (rightEdge > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width - 10;
    }
    if (left < 0) {
      left = 10;
    }
    if (bottomEdge > window.innerHeight) {
      top = window.innerHeight - tooltipRect.height - 10;
    }
    if (top < 0) {
      top = 10;
    }

    setTooltipPosition({ top, left });
  }, [position]);

  // Adjust position if window is resized
  useEffect(() => {
    if (isVisible) {
      window.addEventListener("resize", calculatePosition);
      return () => window.removeEventListener("resize", calculatePosition);
    }
  }, [isVisible, calculatePosition]);

  // Recalculate position after tooltip is rendered and sized
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      calculatePosition();
    }
  }, [isVisible, content, position, calculatePosition]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div
      className="tooltip-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={triggerRef}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`tooltip tooltip-${position} ${className}`}
          style={{
            top:
              tooltipPosition.top === 0 && tooltipPosition.left === 0
                ? "-9999px"
                : `${tooltipPosition.top}px`,
            left:
              tooltipPosition.top === 0 && tooltipPosition.left === 0
                ? "-9999px"
                : `${tooltipPosition.left}px`,
            position: "fixed",
            zIndex: 9999,
            visibility:
              tooltipPosition.top === 0 && tooltipPosition.left === 0 ? "hidden" : "visible",
          }}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
